import React, { ChangeEventHandler, FC, useState } from 'react';

import {
  TEMPLATE__DUE_DATE_OFFSET_AMOUNT_MAX,
  TEMPLATE__DUE_DATE_OFFSET_AMOUNT_MIN,
} from '../../../../constants/forms';
import { DateOffsetInput, TimeUnit } from '../../../../generated/graphql';
import MuiSelect from '../../MuiSelect';

import { getCustomOffsetUnitOptions } from './helpers';
import * as Styled from './styled';

/**
 * @todo Replace when BE translations are added
 */
const AMOUNT = 'Amount';
const UNIT = 'Unit';

type Props = {
  customOffset: DateOffsetInput | null;
  isRequired?: boolean;
  requestChange: (offset: DateOffsetInput) => void;
};

/**
 * OffsetCustom component
 *
 * @param props               Props passed to the component
 * @param props.customOffset  The current custom offset
 * @param props.isRequired    Whether the input is required
 * @param props.requestChange Callback to change the value of the select
 * @returns                   The component itself
 */
const OffsetCustom: FC<Props> = ({
  customOffset,
  isRequired,
  requestChange,
}) => {
  const [timeUnit, setTimeUnit] = useState<TimeUnit>(TimeUnit.MINUTES);
  const options = getCustomOffsetUnitOptions();
  const defaultValue =
    options.find(option => option.value === customOffset?.unit) ?? options[0];

  /**
   * Change offset amount
   *
   * @param event Event that triggered the change
   */
  const handleInputChange: ChangeEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = event => {
    const { value: amount } = event.target;

    requestChange({
      amount: amount ? parseInt(amount, 10) : 0,
      unit: timeUnit,
    });
  };

  /**
   * Change offset unit
   *
   * @param unit New offset unit
   */
  const handleSelectChange = (unit: TimeUnit) => {
    setTimeUnit(unit);
    if (customOffset?.amount) {
      requestChange({ amount: customOffset.amount, unit });
    }
  };

  return (
    <Styled.Wrapper>
      {'+'}
      <Styled.TextField
        aria-label={AMOUNT}
        fullWidth={true}
        hiddenLabel={true}
        onChange={handleInputChange}
        placeholder="5"
        required={isRequired}
        size="small"
        slotProps={{
          htmlInput: {
            max: TEMPLATE__DUE_DATE_OFFSET_AMOUNT_MAX,
            maxLength: 4,
            min: TEMPLATE__DUE_DATE_OFFSET_AMOUNT_MIN,
          },
        }}
        type="number"
        value={customOffset?.amount ?? ''}
      />
      <MuiSelect
        defaultValue={defaultValue}
        isLabelHidden={true}
        label={UNIT}
        options={options}
        requestChange={item => handleSelectChange(item.value as TimeUnit)}
        size="small"
      />
    </Styled.Wrapper>
  );
};

export default OffsetCustom;
